import { Fragment, useMemo, useState } from 'react'
import {
  Box,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Toolbar,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
  useTheme
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ReceiptLong as ReceiptLongIcon
} from '@mui/icons-material'
import { Grid } from '@mui/system'

import { qboChargeNameIsMeaningful } from '../../utils'
import { DRAWER_TOGGLE_Z, DRAWER_TOGGLE_BELOW_BAR_Z } from './dashboardLayerZ'

const parseInvoiceTs = (row) => {
  if (!row?.invoiceDate) return 0
  const d = new Date(row.invoiceDate)
  return Number.isNaN(d.getTime()) ? 0 : d.getTime()
}

// Group-by-month uses the bucket month when a line was prorated across months; otherwise invoice date.
const monthKeyFromRow = (row) => {
  if (row?.billingMonthKey && /^\d{4}-\d{2}$/.test(String(row.billingMonthKey))) {
    return String(row.billingMonthKey)
  }
  const t = parseInvoiceTs(row)
  if (!t) return 'unknown'
  const d = new Date(t)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

const monthTitleFromKey = (key) => {
  if (key === 'unknown') return 'Unknown date'
  const [y, m] = key.split('-').map(Number)
  if (!y || !m) return key
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

const billingRowListKey = (groupKey, row) => {
  const inv = String(row?.invoiceNumber ?? '').trim()
  const chargeSegment = qboChargeNameIsMeaningful(row?.chargeName)
    ? String(row.chargeName).trim()
    : 'undefined'
  return [
    groupKey,
    row?.clientName ?? '',
    inv,
    chargeSegment,
    String(row?.lineType ?? '').trim(),
    row?.billingMonthKey ?? '',
    Number(row?.amount) || 0
  ].join('|')
}

const compareBillingRows = (a, b, orderBy, order) => {
  const mul = order === 'asc' ? 1 : -1
  if (orderBy === 'amount') return mul * ((Number(a.amount) || 0) - (Number(b.amount) || 0))
  if (orderBy === 'invoiceDate') return mul * (parseInvoiceTs(a) - parseInvoiceTs(b))
  const sa = String(a[orderBy] ?? '')
  const sb = String(b[orderBy] ?? '')
  return mul * sa.localeCompare(sb, undefined, { sensitivity: 'base', numeric: true })
}

const BillingDetailsDrawer = ({ props: viewProps = {}, controls = {} }) => {
  const {
    open,
    qboBillingRows,
    billingSelectedClientNames,
    qboRowsByClient,
    qboBillingTotalsByClient,
    clientSettings = {},
    dataTranspositionOpen = false
  } = viewProps

  const flyoutZIndex =
    dataTranspositionOpen && !open ? DRAWER_TOGGLE_BELOW_BAR_Z : DRAWER_TOGGLE_Z
  const { onOpenChange } = controls
  const theme = useTheme()
  const bill = theme.custom.colors.billingDrawer

  const [groupBy, setGroupBy] = useState('client')
  const [orderBy, setOrderBy] = useState('invoiceDate')
  const [order, setOrder] = useState('desc')

  const clientCount = billingSelectedClientNames.length
  const singleClientLayout = clientCount <= 1

  const handleSort = (property) => {
    if (orderBy === property) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    } else {
      setOrderBy(property)
      setOrder(property === 'invoiceDate' ? 'desc' : 'asc')
    }
  }

  const groups = useMemo(() => {
    if (!billingSelectedClientNames.length) return []

    const gb = groupBy === 'charge' ? 'chargeType' : groupBy

    if (gb === 'client') {
      return billingSelectedClientNames.map((clientName) => {
        const rows = [...(qboRowsByClient[clientName] || [])]
        rows.sort((a, b) => compareBillingRows(a, b, orderBy, order))
        const total = qboBillingTotalsByClient[clientName] ?? 0
        return {
          key: `client-${clientName}`,
          title: clientName,
          subtitle: null,
          total,
          rows
        }
      })
    }

    const allRows = []
    billingSelectedClientNames.forEach((clientName) => {
      ;(qboRowsByClient[clientName] || []).forEach((row) => {
        allRows.push({ ...row, clientName })
      })
    })

    if (gb === 'month') {
      const byMonth = new Map()
      allRows.forEach((row) => {
        const mk = monthKeyFromRow(row)
        if (!byMonth.has(mk)) byMonth.set(mk, [])
        byMonth.get(mk).push(row)
      })
      return [...byMonth.entries()]
        .sort(([a], [b]) => (a < b ? 1 : a > b ? -1 : 0))
        .map(([mk, rows]) => {
          const sorted = [...rows].sort((a, b) => compareBillingRows(a, b, orderBy, order))
          const total = sorted.reduce((s, r) => s + (Number(r.amount) || 0), 0)
          return {
            key: `month-${mk}`,
            title: monthTitleFromKey(mk),
            subtitle: mk === 'unknown' ? null : mk,
            total,
            rows: sorted
          }
        })
    }

    const byLineType = new Map()
    allRows.forEach((row) => {
      const tk = (row.lineType && String(row.lineType).trim()) || '—'
      if (!byLineType.has(tk)) byLineType.set(tk, [])
      byLineType.get(tk).push(row)
    })
    return [...byLineType.entries()]
      .sort(([a], [b]) => String(a).localeCompare(String(b)))
      .map(([lineType, rows]) => {
        const sorted = [...rows].sort((a, b) => compareBillingRows(a, b, orderBy, order))
        const total = sorted.reduce((s, r) => s + (Number(r.amount) || 0), 0)
        return {
          key: `lineType-${lineType}`,
          title: lineType,
          subtitle: null,
          total,
          rows: sorted
        }
      })
  }, [billingSelectedClientNames, qboRowsByClient, qboBillingTotalsByClient, groupBy, orderBy, order])

  const showClientColumn = groupBy !== 'client'

  const headCell = (id, label, align = 'left', accentColor = bill.accent, cellSx = {}) => (
    <TableCell
      align={align}
      sortDirection={orderBy === id ? order : false}
      sx={{
        fontWeight: 800,
        color: bill.text,
        backgroundColor: `${bill.surface} !important`,
        borderBottom: `1px solid ${alpha(accentColor, 0.25)}`,
        ...cellSx
      }}
    >
      <TableSortLabel
        active={orderBy === id}
        direction={orderBy === id ? order : 'asc'}
        onClick={() => handleSort(id)}
        sx={{
          '&.MuiTableSortLabel-root': { color: bill.text },
          '& .MuiTableSortLabel-icon': { color: `${accentColor} !important` }
        }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  )

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          right: { xs: open ? '100%' : 0, sm: open ? '75vw' : 0 },
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: flyoutZIndex,
          transition: 'right 0.3s ease-in-out'
        }}
      >
        <IconButton
          onClick={() => onOpenChange((v) => !v)}
          aria-label={open ? 'Close billing drawer' : 'Open billing drawer'}
          sx={{
            backgroundColor: alpha(bill.accent, 0.92),
            color: theme.palette.common.white,
            borderRadius: '8px 0 0 8px',
            width: 48,
            height: 56,
            minWidth: 48,
            minHeight: 48,
            boxShadow: `0 4px 14px ${alpha(bill.accent, 0.45)}`,
            '&:hover': {
              backgroundColor: alpha(bill.accent, 1),
              boxShadow: `0 6px 18px ${alpha(bill.accent, 0.55)}`
            },
            '@media (min-width: 600px)': { height: 64 }
          }}
        >
          {open ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>

      <Drawer
        anchor='right'
        open={open}
        variant='persistent'
        sx={{
          width: open ? { xs: '100vw', sm: '75vw' } : 0,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: { xs: '100vw', sm: '75vw' },
            minWidth: { sm: 420 },
            maxWidth: 1600,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            backgroundColor: bill.background,
            backgroundImage: `linear-gradient(165deg, ${alpha(bill.accent, 0.12)} 0%, transparent 42%)`,
            borderLeft: `1px solid ${alpha(bill.accent, 0.45)}`,
            boxShadow: `-12px 0 40px ${alpha('#000', 0.55)}`
          }
        }}
      >
        <Toolbar
          sx={{
            minHeight: '64px !important',
            flexShrink: 0,
            background: `linear-gradient(90deg, ${alpha(bill.accent, 0.18)} 0%, ${alpha(bill.surface, 1)} 100%)`,
            borderBottom: `1px solid ${alpha(bill.accent, 0.35)}`,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            px: 2,
            py: 1.5
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: { xs: 1.5, sm: 2 },
              flex: 1,
              minWidth: 0,
              rowGap: 1.25
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, minWidth: 0 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: alpha(bill.accent, 0.25),
                  border: `1px solid ${alpha(bill.accent, 0.5)}`,
                  flexShrink: 0
                }}
              >
                <ReceiptLongIcon sx={{ color: bill.accent, fontSize: 24 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant='h6' sx={{ fontWeight: 800, lineHeight: 1.2, color: bill.text }}>
                  Billing Details
                </Typography>
                <Typography variant='caption' sx={{ color: bill.muted }}>
                  QuickBooks · same filters as the main chart
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 0.5,
                minWidth: { xs: '100%', sm: 220 },
                pl: { xs: 0, sm: 2 },
                borderLeft: { xs: 'none', sm: `1px solid ${alpha(bill.accent, 0.28)}` }
              }}
            >
              <Typography variant='caption' sx={{ color: bill.muted, fontWeight: 700, letterSpacing: 0.3 }}>
                Group by
              </Typography>
              <ToggleButtonGroup
                exclusive
                size='small'
                value={groupBy === 'charge' ? 'chargeType' : groupBy}
                onChange={(e, v) => v && setGroupBy(v)}
                sx={{ flexWrap: 'wrap', gap: 0.5 }}
              >
                <ToggleButton value='client' sx={{ textTransform: 'none', fontWeight: 700 }}>
                  Client
                </ToggleButton>
                <ToggleButton value='month' sx={{ textTransform: 'none', fontWeight: 700 }}>
                  Month
                </ToggleButton>
                <ToggleButton value='chargeType' sx={{ textTransform: 'none', fontWeight: 700 }}>
                  Charge type
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          <IconButton
            onClick={() => onOpenChange(false)}
            size='small'
            sx={{
              color: bill.text,
              backgroundColor: alpha('#fff', 0.06),
              flexShrink: 0,
              alignSelf: 'flex-start',
              '&:hover': { backgroundColor: alpha('#fff', 0.12) }
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Toolbar>

        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Box sx={{ flexShrink: 0, px: 2, pt: 1.5, pb: 1 }}>
            <Typography variant='caption' sx={{ color: bill.muted, display: 'block' }}>
              {qboBillingRows.length} line item{qboBillingRows.length === 1 ? '' : 's'} · {clientCount} client{clientCount === 1 ? '' : 's'}
            </Typography>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              px: 2,
              pb: 2,
              width: '100%',
              boxSizing: 'border-box'
            }}
          >
            {!billingSelectedClientNames.length && (
              <Typography variant='body2' sx={{ color: bill.muted, py: 2 }}>
                Select at least one client on the dashboard to see billing lines.
              </Typography>
            )}

            <Grid
              container
              spacing={2}
              sx={{
                width: '100%',
                alignItems: 'stretch',
                boxSizing: 'border-box',
                '& > .MuiGrid-item': { maxWidth: '100%' }
              }}
            >
              {groups.map((g) => {
                const groupAccent =
                  groupBy === 'client'
                    ? clientSettings?.[g.title]?.color || bill.accent
                    : bill.accent
                const monthKeysForAvg = new Set(g.rows.map((r) => monthKeyFromRow(r)))
                const monthCountForAvg = Math.max(1, monthKeysForAvg.size)
                const avgPerMonth = Number(g.total || 0) / monthCountForAvg
                const chargeTotals = {}
                g.rows.forEach((r) => {
                  const tk = (r.lineType && String(r.lineType).trim()) || '—'
                  chargeTotals[tk] = (chargeTotals[tk] || 0) + (Number(r.amount) || 0)
                })
                const chargeEntries = Object.entries(chargeTotals).sort((a, b) => b[1] - a[1])
                const totalAmt = Number(g.total || 0)
                const showChargePct = groupBy !== 'charge' && chargeEntries.length > 1 && totalAmt > 0
                return (
                  <Grid key={g.key} item xs={12} sx={{ width: '100%', minWidth: 0 }}>
                    <Accordion
                      defaultExpanded
                      disableGutters
                      elevation={0}
                      sx={{
                        width: '100%',
                        borderRadius: 2,
                        border: `1px solid ${bill.border}`,
                        borderLeft: `4px solid ${groupAccent}`,
                        backgroundColor: alpha(bill.surface, 0.85),
                        boxShadow: `inset 0 1px 0 ${alpha('#fff', 0.04)}`,
                        '&:before': { display: 'none' },
                        '& .MuiAccordionSummary-root': { minHeight: 52 }
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: groupAccent }} />}
                        sx={{
                          backgroundColor: alpha(groupAccent, 0.1),
                          borderBottom: `1px solid ${alpha(groupAccent, 0.22)}`,
                          minHeight: 52,
                          flexShrink: 0,
                          '& .MuiAccordionSummary-content': {
                            alignItems: 'flex-start',
                            mr: 1,
                            flexWrap: 'nowrap',
                            width: '100%',
                            minWidth: 0,
                            overflow: 'hidden'
                          }
                        }}
                      >
                        <Box sx={{ width: '100%', minWidth: 0, pr: 0.5 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'baseline',
                              justifyContent: 'space-between',
                              gap: 2,
                              width: '100%',
                              minWidth: 0
                            }}
                          >
                            <Typography
                              variant='subtitle1'
                              sx={{
                                fontWeight: 800,
                                color: bill.text,
                                minWidth: 0,
                                flex: '1 1 auto',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {g.title}
                            </Typography>
                            <Typography
                              component='div'
                              variant='subtitle1'
                              sx={{
                                color: bill.muted,
                                flexShrink: 0,
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'baseline',
                                justifyContent: 'flex-end',
                                columnGap: 0.25,
                                rowGap: 0.25,
                                textAlign: 'right',
                                maxWidth: '55%'
                              }}
                            >
                              <Box component='span' sx={{ fontWeight: 700 }}>Total:</Box>
                              {' '}
                              <Box
                                component='span'
                                sx={{
                                  fontWeight: 800,
                                  fontVariantNumeric: 'tabular-nums',
                                  color: totalAmt !== 0 ? groupAccent : bill.muted
                                }}
                              >
                                {Number(g.total || 0).toLocaleString(undefined, {
                                  style: 'currency',
                                  currency: 'USD',
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}
                              </Box>
                              <Box component='span' sx={{ color: bill.muted, px: 0.5 }}>·</Box>
                              <Box component='span' sx={{ fontWeight: 700 }}>Avg/month:</Box>
                              {' '}
                              <Box
                                component='span'
                                sx={{
                                  fontWeight: 800,
                                  fontVariantNumeric: 'tabular-nums',
                                  color: avgPerMonth !== 0 ? groupAccent : bill.muted
                                }}
                              >
                                {avgPerMonth.toLocaleString(undefined, {
                                  style: 'currency',
                                  currency: 'USD',
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}
                              </Box>
                            </Typography>
                          </Box>
                          {g.subtitle && (
                            <Typography variant='caption' sx={{ color: bill.muted, display: 'block', mt: 0.35 }}>
                              {g.subtitle}
                            </Typography>
                          )}
                          {showChargePct && (
                            <Typography
                              component='div'
                              variant='caption'
                              sx={{
                                mt: 0.35,
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'baseline',
                                columnGap: 0.25,
                                rowGap: 0.35
                              }}
                            >
                              {chargeEntries.map(([name, amt], idx) => {
                                const pct = (amt / totalAmt) * 100
                                const nz = Number(amt) !== 0
                                return (
                                  <Fragment key={`${name}-${idx}`}>
                                    {idx > 0 ? <Box component='span' sx={{ color: bill.muted, px: 0.5 }}>·</Box> : null}
                                    <Box component='span' sx={{ fontWeight: 700, color: bill.muted }}>{name}</Box>
                                    <Box component='span' sx={{ color: bill.muted, px: 0.25 }}>·</Box>
                                    <Box
                                      component='span'
                                      sx={{
                                        fontWeight: 800,
                                        fontVariantNumeric: 'tabular-nums',
                                        color: nz ? groupAccent : bill.muted
                                      }}
                                    >
                                      {pct.toFixed(1)}%
                                    </Box>
                                    <Box component='span' sx={{ color: bill.muted, px: 0.25 }}>·</Box>
                                    <Box
                                      component='span'
                                      sx={{
                                        fontWeight: 800,
                                        fontVariantNumeric: 'tabular-nums',
                                        color: nz ? groupAccent : bill.muted
                                      }}
                                    >
                                      {Number(amt).toLocaleString(undefined, {
                                        style: 'currency',
                                        currency: 'USD',
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                      })}
                                    </Box>
                                  </Fragment>
                                )
                              })}
                            </Typography>
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails
                        sx={{
                          p: 0,
                          backgroundColor: alpha('#000', 0.15),
                          display: 'block'
                        }}
                      >
                        <TableContainer
                          sx={{
                            maxHeight: singleClientLayout
                              ? 'min(65vh, calc(100vh - 260px))'
                              : 'min(42vh, 380px)',
                            overflow: 'auto',
                            width: '100%'
                          }}
                        >
                          <Table size='small' stickyHeader>
                            <TableHead>
                              <TableRow>
                                {showClientColumn && headCell('clientName', 'Client', 'left', groupAccent)}
                                {headCell('invoiceDate', 'Invoice date', 'left', groupAccent)}
                                {headCell('chargeName', 'Charge', 'left', groupAccent)}
                                {headCell('lineType', 'Type', 'left', groupAccent)}
                                {headCell('customerName', 'QBO customer', 'left', groupAccent, {
                                  maxWidth: 140,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                })}
                                {headCell('invoiceNumber', 'Invoice #', 'left', groupAccent, {
                                  maxWidth: 112,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                })}
                                {headCell('amount', 'Amount', 'right', groupAccent)}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {g.rows.map((row) => (
                                <TableRow
                                  key={billingRowListKey(g.key, row)}
                                  sx={{
                                    '&:nth-of-type(even)': { backgroundColor: alpha('#fff', 0.02) },
                                    '&:hover': { backgroundColor: alpha(groupAccent, 0.08) }
                                  }}
                                >
                                  {showClientColumn && (
                                    <TableCell sx={{ color: bill.text, borderColor: bill.border, fontSize: '0.8rem', fontWeight: 600 }}>
                                      {row.clientName}
                                    </TableCell>
                                  )}
                                  <TableCell sx={{ color: bill.muted, borderColor: bill.border, fontSize: '0.8rem' }}>
                                    {row.invoiceDate ? new Date(row.invoiceDate).toLocaleDateString('en-US') : ''}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      color: bill.text,
                                      borderColor: bill.border,
                                      fontSize: '0.8rem',
                                      lineHeight: 1.35,
                                      wordBreak: 'break-word',
                                      overflowWrap: 'anywhere',
                                      minWidth: 200
                                    }}
                                    title={row.chargeName || ''}
                                  >
                                    {row.chargeName}
                                  </TableCell>
                                  <TableCell sx={{ color: bill.muted, fontSize: '0.75rem', borderColor: bill.border }}>{row.lineType || '—'}</TableCell>
                                  <TableCell
                                    sx={{
                                      maxWidth: 140,
                                      color: bill.muted,
                                      borderColor: bill.border,
                                      fontSize: '0.8rem',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                    title={row.customerName || ''}
                                  >
                                    {row.customerName}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      maxWidth: 112,
                                      color: bill.muted,
                                      borderColor: bill.border,
                                      fontSize: '0.8rem',
                                      fontVariantNumeric: 'tabular-nums',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                    title={row.invoiceNumber || ''}
                                  >
                                    {row.invoiceNumber}
                                  </TableCell>
                                  <TableCell
                                    align='right'
                                    sx={{
                                      fontVariantNumeric: 'tabular-nums',
                                      color: Number(row.amount || 0) !== 0 ? groupAccent : bill.muted,
                                      fontWeight: 700,
                                      borderColor: bill.border,
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {Number(row.amount || 0).toLocaleString(undefined, {
                                      style: 'currency',
                                      currency: 'USD',
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2
                                    })}
                                  </TableCell>
                                </TableRow>
                              ))}
                              {!g.rows.length && (
                                <TableRow>
                                  <TableCell
                                    colSpan={showClientColumn ? 7 : 6}
                                    sx={{ color: bill.muted, py: 3, textAlign: 'center', border: 'none' }}
                                  >
                                    No line items in this group for the current filters.
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )
              })}
            </Grid>
          </Box>
        </Box>
      </Drawer>
    </>
  )
}

export { BillingDetailsDrawer }
